/**
 * 错题复习 / 今日复习
 * ------------------------------------------------------------
 * 以「错题聚合记录」为单位，把 W/S/G 三类错题重新出成题，
 * 连续答对 2 次即标记为已掌握（逻辑在 services/records.js）。
 */
import { state } from '../core/store.js';
import { dueErrors, recentErrors, errorsOf, studyStreak } from '../services/records.js';
import { getUnitContent } from '../services/dataSource.js';
import { PracticeSession } from '../engines/practiceEngine.js';
import { makeQuestion } from '../core/models.js';
import { WORD_LEVELS } from '../engines/generators/wordLevels.js';
import { SENTENCE_LEVELS } from '../engines/generators/sentenceLevels.js';
import { card, emptyState, statRow, tag, confirmDialog } from '../components/ui.js';
import { navigate } from '../core/router.js';
import { escapeHtml, autoSpeak, todayKey } from '../utils/index.js';
import { playWord, playSentenceEn } from '../services/audioService.js';

export default async function renderReview(mount) {
  const [due, streak, recent] = await Promise.all([dueErrors(), studyStreak(), recentErrors(3)]);

  const bySkill = { W: [], S: [], G: [], R: [] };
  due.forEach(e => { (bySkill[e.skill] || bySkill.G).push(e); });

  mount.innerHTML = `
    <div class="page-head">
      <h1>🔁 错题复习</h1>
      <p class="lead">系统把错过的知识点重新出成题。连续答对 2 次后才会标记为「已掌握」。</p>
    </div>

    ${card(statRow([
      { label: '待复习知识点', value: due.length, unit: ' 个' },
      { label: '最近 3 天出错', value: recent.length, unit: ' 个' },
      { label: '连续学习', value: streak, unit: ' 天' },
      { label: '单词类', value: bySkill.W.length },
      { label: '句子类', value: bySkill.S.length },
      { label: '语法类', value: bySkill.G.length },
    ]), {})}

    ${due.length ? `
      <div class="row mt-8" style="gap:10px;flex-wrap:wrap">
        <button class="btn btn-primary" id="review-all" type="button">开始复习全部 (${Math.min(due.length, 20)})</button>
        ${bySkill.W.length ? `<button class="btn btn-ghost" data-review="W" type="button">只复习单词 (${bySkill.W.length})</button>` : ''}
        ${bySkill.S.length ? `<button class="btn btn-ghost" data-review="S" type="button">只复习句子 (${bySkill.S.length})</button>` : ''}
        ${bySkill.G.length ? `<button class="btn btn-ghost" data-review="G" type="button">只复习语法 (${bySkill.G.length})</button>` : ''}
      </div>

      <div class="mt-8">
        ${card(`
          <div class="table-wrap"><table class="tbl">
            <thead><tr><th>类型</th><th>级别</th><th>内容</th><th>正确答案</th><th>错误次数</th><th>最近出错</th></tr></thead>
            <tbody>
              ${due.map(e => `
                <tr>
                  <td><span class="tag ${e.skill === 'W' ? 'blue' : e.skill === 'S' ? 'green' : 'orange'}">${skillName(e.skill)}</span></td>
                  <td>L${e.level}</td>
                  <td style="max-width:340px">${escapeHtml(String(e.prompt || '').replace(/\n/g, ' ').slice(0, 100))}</td>
                  <td class="en">${escapeHtml(e.expected || '—')}
                    ${e.skill === 'W' ? `<button class="icon-btn" data-en="${escapeHtml(e.expected || '')}" data-unit="${e.unitId || ''}" data-ref-type="${e.refType || 'word'}" data-ref-id="${escapeHtml(e.refId || '')}" type="button">🔊</button>` : ''}
                  </td>
                  <td><b class="sym-no">${e.wrongCount}</b></td>
                  <td class="muted nowrap">${e.lastSeenAt ? e.lastSeenAt.slice(0, 10) : '—'}</td>
                </tr>`).join('')}
            </tbody>
          </table></div>`, { title: `待复习清单（${due.length}）` })}
      </div>`
    : card(emptyState('🎉', '暂时没有待复习的错题', '保持住！也可以主动去做新练习',
        '<a class="btn btn-primary" href="#/practice">去练习</a>'))}
  `;

  mount.querySelectorAll('[data-speak]').forEach(b => b.addEventListener('click', () => autoSpeak(b.dataset.speak)));
  mount.querySelectorAll('[data-en]').forEach(b => {
    b.addEventListener('click', async () => {
      const en = b.dataset.en;
      const unitId = b.dataset.unit;
      const refType = b.dataset.refType;
      const refId = b.dataset.refId;
      let audio = '';
      if (unitId && refType === 'word') {
        try {
          const c = await getUnitContent({ textbookId: state.selection.textbookId, unitId });
          const w = (c.words || []).find(w => String(w.id) === refId || w.en === en);
          audio = w?.audio || '';
        } catch { /* fallback */ }
      }
      if (audio) playWord({ en, audio });
      else if (refType === 'sentence') playSentenceEn(en);
      else playWord({ en });
    });
  });
  mount.querySelector('#review-all')?.addEventListener('click', () => startReview(due, 'all'));
  mount.querySelectorAll('[data-review]').forEach(btn => {
    btn.addEventListener('click', () => {
      const skill = btn.dataset.review;
      startReview(due.filter(e => e.skill === skill), skill);
    });
  });
}

function skillName(s) {
  return { W: '单词', S: '句子', G: '语法', R: '记忆' }[s] || '语法';
}

/**
 * 用错题记录重新构造题目，写入 sessionStorage 后跳答题页
 * 做法：把错题还原成「同级别同类题型」，直接用已有的生成器按 refId 定位到原知识点。
 */
async function startReview(errors, scope) {
  if (!errors.length) return;
  const list = errors.slice(0, 20);

  // 按单元分组取内容
  const unitIds = [...new Set(list.map(e => e.unitId).filter(Boolean))];
  const contents = new Map();
  for (const uid of unitIds) {
    contents.set(uid, await getUnitContent(state.selection.textbookId, uid));
  }

  const questions = [];
  for (const e of list) {
    const content = contents.get(e.unitId);
    if (!content) continue;
    const q = rebuildQuestion(e, content);
    if (q) questions.push(q);
  }

  if (!questions.length) {
    alert('这些错题对应的内容已不在教材中（可能被删除了），无法重新出题。');
    return;
  }

  sessionStorage.setItem('pending-session', JSON.stringify({
    generatorId: '__review__',
    module: 'review',
    count: questions.length,
    unitId: questions[0].unitId,
    textbookId: state.selection.textbookId,
    title: scope === 'all' ? '错题复习' : `${skillName(scope)}错题复习`,
    presetQuestions: questions.map(q => ({ ...q })),
    at: Date.now(),
  }));
  navigate('#/session');
}

/** 依据错题记录重建一道题（尽量保持原级别） */
function rebuildQuestion(err, content) {
  if (err.refType === 'word') {
    const w = content.words.find(x => x.id === err.refId);
    if (!w) return null;
    const lv = Math.max(1, Math.min(6, Number(err.level) || 1));
    return buildWordQuestion(w, content.words, lv);
  }
  if (err.refType === 'sentence') {
    const s = content.sentences.find(x => x.id === err.refId);
    if (!s) return null;
    const lv = Math.max(1, Math.min(8, Number(err.level) || 5));
    return buildSentenceQuestion(s, content.sentences, lv);
  }
  // 语法：用原题干重建
  return makeQuestion({
    textbookId: err.textbookId,
    unitId: err.unitId,
    type: 'fill',
    skill: 'G',
    level: err.level,
    prompt: err.prompt,
    answer: [err.expected],
    explain: '',
    refType: 'grammar',
    refId: err.refId,
    origin: 'generated',
    status: 'confirmed',
  });
}

/* 复用生成器逻辑（简单版，避免重复实现） */
function buildWordQuestion(w, pool, level) {
  const others = pool.filter(x => x.id !== w.id);
  const pickN = n => others.slice(0, n);
  const base = {
    textbookId: w.textbookId, unitId: w.unitId, refType: 'word', refId: w.id,
    level, skill: 'W', origin: 'generated', status: 'confirmed',
  };
  if (level === 1) {
    return makeQuestion({ ...base, type: 'listen', prompt: '听发音，选出你听到的单词', audio: w.en,
      options: [w.en, ...pickN(3).map(o => o.en)], answer: [w.en], explain: `${w.en} = ${w.zh}` });
  }
  if (level === 2) {
    return makeQuestion({ ...base, type: 'choice', prompt: `“${w.en}” 的中文意思是？`,
      options: [w.zh, ...pickN(3).map(o => o.zh)], answer: [w.zh], explain: `${w.en} = ${w.zh}` });
  }
  if (level === 3) {
    return makeQuestion({ ...base, type: 'choice', prompt: `“${w.zh}” 对应的英文是？`,
      options: [w.en, ...pickN(3).map(o => o.en)], answer: [w.en], explain: `${w.en} ${w.phonetic || ''}` });
  }
  return makeQuestion({ ...base, type: 'spell', prompt: `看中文写单词：${w.zh}`, answer: [w.en],
    explain: `${w.en} ${w.phonetic || ''}`, placeholder: level <= 4 ? `${w.en[0]}${'_'.repeat(Math.max(0, w.en.length - 1))}` : '' });
}

function buildSentenceQuestion(s, pool, level) {
  const base = {
    textbookId: s.textbookId, unitId: s.unitId, refType: 'sentence', refId: s.id,
    level, skill: 'S', origin: 'generated', status: 'confirmed',
  };
  if (level === 1) {
    return makeQuestion({ ...base, type: 'choice', prompt: `“${s.en}” 的意思是？`,
      options: [s.zh, ...pool.filter(x => x.id !== s.id).slice(0, 3).map(x => x.zh)], answer: [s.zh], explain: `${s.en} = ${s.zh}` });
  }
  if (level === 2) {
    return makeQuestion({ ...base, type: 'judge', prompt: `判断翻译是否正确：\n${s.en}\n→ ${s.zh}`,
      options: ['正确', '错误'], answer: ['正确'], explain: `${s.en} = ${s.zh}` });
  }
  if (level === 5) {
    return makeQuestion({ ...base, type: 'order', prompt: '把单词排成正确的句子',
      options: s.en.split(/\s+/), tokens: s.en.split(/\s+/), answer: [s.en], explain: `${s.en}\n${s.zh}` });
  }
  return makeQuestion({ ...base, type: 'transform', prompt: `把中文翻译成英文：${s.zh}`, answer: [s.en], explain: `${s.en}` });
}
